import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Popover,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import toast from 'react-hot-toast';
import MDButton from 'components/MDButton';
import PermissionControl from 'components/Utils/PermissionControl';
import useApi from 'components/Utils/api';
import { useTranslation } from 'react-i18next';

function SettlementRouteAssignment({ merchant, onAssignmentChange }) {
  const { t } = useTranslation();
  const api = useApi();

  // State management
  const [routes, setRoutes] = useState([]);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [changeRouteId, setChangeRouteId] = useState('');
  const [changingRoute, setChangingRoute] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get merchant MID
  const getMerchantMid = () => merchant?.mid || merchant?.merchant?.mid || null;

  // Load all active settlement routes
  const loadSettlementRoutes = () => {
    api
      .get('settlement-routes')
      .then((res) => {
        setRoutes(res.data || []);
      })
      .catch(() => {
        // non-fatal
      })
      .finally(() => setLoading(false));
  };

  // Load current settlement assignment for merchant
  const loadSettlementAssignment = (mid) => {
    if (!mid) return;

    api
      .get('settlement-routes/merchants')
      .then((res) => {
        const gwMerchants = res.data || [];
        const found = gwMerchants.find((m) => m.mid === mid);
        if (!found) return;

        return api
          .get(`settlement-routes/assignments?merchantId=${found.id}`)
          .then((aRes) => {
            const assignments = aRes.data || [];
            if (assignments.length > 0) {
              const a = assignments.find((s) => s.status === 'ACTIVE');
              const route = routes.find((r) => r.id === a?.settlementRouteId);
              const assignment = {
                routeId: a?.settlementRouteId,
                routeName: route?.name || a?.settlementRouteId,
                settlementMode: route?.settlementMode || 'INTERNAL',
              };
              setCurrentAssignment(assignment);
              if (onAssignmentChange) {
                onAssignmentChange(assignment);
              }
            }
          });
      })
      .catch(() => {
        // non-fatal
      });
  };

  // Handle route change
  const handleChangeRoute = () => {
    const mid = getMerchantMid();
    if (!changeRouteId || !mid) {
      toast.error('Please select a settlement route first.');
      return;
    }

    setChangingRoute(true);

    // First, get the gateway merchant ID
    api
      .get('settlement-routes/merchants')
      .then((res) => {
        const gwMerchants = res.data || [];
        const found = gwMerchants.find((m) => m.mid === mid);
        if (!found) {
          toast.error('Could not find merchant in settlement system');
          setChangingRoute(false);
          return;
        }

        // Now update the assignment
        return api
          .post('settlement-routes/assignments', {
            routeId: changeRouteId,
            merchantIds: [found.id],
            isDefault: true,
          })
          .then(() => {
            toast.success('Settlement route updated');
            setChangeRouteId('');
            setAnchorEl(null);
            loadSettlementAssignment(mid);
          });
      })
      .catch((err) => toast.error(err?.message || 'Failed to update settlement route'))
      .finally(() => setChangingRoute(false));
  };

  // Load routes on mount
  useEffect(() => {
    loadSettlementRoutes();
  }, []);

  // Load assignment when merchant or routes change
  useEffect(() => {
    const mid = getMerchantMid();
    if (mid && routes.length > 0) {
      loadSettlementAssignment(mid);
    } else if (!mid || routes.length === 0) {
      // No merchant or routes, notify parent of default assignment
      if (onAssignmentChange) {
        onAssignmentChange(null);
      }
    }
  }, [merchant, routes]);

  const handleOpenPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setChangeRouteId('');
  };

  const open = Boolean(anchorEl);
  const isExternalRoute = currentAssignment?.settlementMode === 'EXTERNAL';

  return (
    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} mt={1} mb={1} px={1}>
      <Typography variant="caption" color="text.secondary" fontWeight="medium">
        Settlement Route:
      </Typography>

      {currentAssignment ? (
        <>
          <Typography variant="caption" fontWeight="bold">
            {currentAssignment.routeName}
          </Typography>
          <Chip
            label={currentAssignment.settlementMode || 'INTERNAL'}
            color={currentAssignment.settlementMode === 'EXTERNAL' ? 'warning' : 'default'}
            size="small"
          />
          <PermissionControl permission={{ restrict: true, module: 'Merchants', subModule: 'Update' }}>
            <IconButton size="small" onClick={handleOpenPopover} title="Edit settlement route">
              <EditIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </PermissionControl>
        </>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary">
            Default
          </Typography>
          <PermissionControl permission={{ restrict: true, module: 'Merchants', subModule: 'Update' }}>
            <IconButton size="small" onClick={handleOpenPopover} title="Assign settlement route">
              <EditIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </PermissionControl>
        </>
      )}

      {/* Popover for route selection */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{ '& .MuiPopover-paper' : { bgcolor: 'background.paper' } }}
      >
        <Box sx={{ p: 2, minWidth: 300 }}>
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            Assign Settlement Route
          </Typography>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            {/* <InputLabel id="change-route-label">Select Route</InputLabel> */}
            <Select
              labelId="change-route-label"
              label="Select Route"
              value={changeRouteId}
              onChange={(e) => setChangeRouteId(e.target.value)}
              displayEmpty

            >
              <MenuItem value="">— select new route —</MenuItem>
              {routes.filter((r) => r.status === 'ACTIVE').map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                  {r.settlementMode === 'EXTERNAL' && ' (EXTERNAL)'}
                  {r.isDefault && ' ★'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box display="flex" gap={1} justifyContent="flex-end">
            <MDButton
              variant="outlined"
              color="secondary"
              size="small"
              onClick={handleClosePopover}
            >
              Cancel
            </MDButton>
            <MDButton
              variant="outlined"
              color="info"
              size="small"
              onClick={handleChangeRoute}
              disabled={changingRoute || !changeRouteId}
            >
              {changingRoute ? 'Saving…' : 'Save Route'}
            </MDButton>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}

SettlementRouteAssignment.propTypes = {
  merchant: PropTypes.shape({
    mid: PropTypes.string,
    merchant: PropTypes.shape({
      mid: PropTypes.string,
    }),
  }).isRequired,
  onAssignmentChange: PropTypes.func,
};

SettlementRouteAssignment.defaultProps = {
  onAssignmentChange: null,
};

export default SettlementRouteAssignment;
